import dayjs from 'dayjs'
import { warn } from '../utils/logger'
import relativeTime from 'dayjs/plugin/relativeTime'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import md5 from 'md5'

dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)

// Currency formatting
export const formatPrice = (
    amount: number | null | undefined,
    currency: string = 'USD',
    locale: string = 'en-US'
): string => {
    if (amount === null || amount === undefined) {
        return 'N/A'
    }

    if (amount === 0) {
        return 'Free'
    }

    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount)
    } catch (error) {
        // Fallback for unsupported currencies/locales
        const symbol = currency === 'EUR' ? '€' : '$'
        return `${symbol}${amount.toFixed(2)}`
    }
}

// Date formatting
export const formatDate = (date: string | Date, format?: string): string => {
    if (!date) return 'N/A'

    const d = dayjs(date)
    return format ? d.format(format) : d.format('MMM D, YYYY')
}

/**
 * Safely formats a date string to a localized date string
 * @param dateString - The date string to format
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns Formatted date string or 'Unknown date' if parsing fails
 */
export const safeFormatDate = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) {
            return 'Unknown date'
        }
        return date.toLocaleDateString(undefined, options)
    } catch (error) {
        console.error('Error parsing date:', dateString, error)
        return 'Unknown date'
    }
}

/**
 * Safely formats a date string to a localized date and time string
 * @param dateString - The date string to format
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns Formatted date and time string or 'Unknown date' if parsing fails
 */
export const safeFormatDateTime = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) {
            return 'Unknown date'
        }
        return date.toLocaleString('nl-NL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            ...options
        },)
    } catch (error) {
        console.error('Error parsing date:', dateString, error)
        return 'Unknown date'
    }
}

export const formatRelativeTime = (date: string | Date): string => {
    if (!date) return 'N/A'

    return dayjs(date).fromNow()
}

// Discount calculation
export const calculateDiscount = (
    originalPrice: number,
    currentPrice: number
): number => {
    if (originalPrice <= 0 || currentPrice >= originalPrice) {
        return 0
    }

    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
}

// Platform color mapping
export const getPlatformColor = (platformSlug: string): string => {
    const colors: Record<string, string> = {
        playstation: '#0070f3',
        xbox: '#107c10',
        nintendo: '#e60012',
        steam: '#1b2838',
        epic: '#313131',
        gog: '#86328a',
        origin: '#f56500',
        uplay: '#0f7fe6',
    }

    return colors[platformSlug] || '#666666'
}

// Image URL helpers
export const getImageUrl = (
    path?: string,
    size: 'small' | 'medium' | 'large' = 'medium'
): string => {
    if (!path) {
        return `/images/placeholder-game-${size}.jpg`
    }

    // If it's already a full URL, return as-is
    if (path.startsWith('http')) {
        return path
    }

    // Otherwise, construct the URL
    return `/images/${size}/${path}`
}

// Slug generation
export const createSlug = (text: string): string => {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
        .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

// URL helpers
export const createGameUrl = (gameSlug: string, platformSlug?: string): string => {
    return platformSlug
        ? `/game/${gameSlug}/${platformSlug}`
        : `/game/${gameSlug}`
}

export const createPlatformUrl = (platformSlug: string): string => {
    return `/platform/${platformSlug}`
}

// Search highlighting
export const highlightSearchTerm = (text: string, searchTerm: string): string => {
    if (!searchTerm) return text

    const regex = new RegExp(`(${searchTerm})`, 'gi')
    return text.replace(regex, '<mark>$1</mark>')
}

// Debounce utility
export const debounce = <T extends (...args: unknown[]) => unknown>(
    func: T,
    delay: number
): ((...args: Parameters<T>) => void) => {
    let timeoutId: number

    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId)
        timeoutId = window.setTimeout(() => func(...args), delay)
    }
}

// Local storage helpers
export const storage = {
    get: <T>(key: string, defaultValue: T): T => {
        try {
            const item = localStorage.getItem(key)
            return item ? JSON.parse(item) : defaultValue
        } catch {
            return defaultValue
        }
    },

    set: <T>(key: string, value: T): void => {
        try {
            localStorage.setItem(key, JSON.stringify(value))
        } catch (error) {
            warn('Failed to save to localStorage:', error)
        }
    },

    remove: (key: string): void => {
        try {
            localStorage.removeItem(key)
        } catch (error) {
            warn('Failed to remove from localStorage:', error)
        }
    },
}

// Validation helpers
export const validation = {
    email: (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    },

    password: (password: string): { isValid: boolean; errors: string[] } => {
        const errors: string[] = []

        if (password.length < 8) {
            errors.push('Password must be at least 8 characters long')
        }

        if (!/[A-Z]/.test(password)) {
            errors.push('Password must contain at least one uppercase letter')
        }

        if (!/[a-z]/.test(password)) {
            errors.push('Password must contain at least one lowercase letter')
        }

        if (!/\d/.test(password)) {
            errors.push('Password must contain at least one number')
        }

        return {
            isValid: errors.length === 0,
            errors,
        }
    },
}

// Number formatting
export const formatNumber = (num: number, compact = false): string => {
    if (compact && num >= 1000) {
        return new Intl.NumberFormat('en-US', {
            notation: 'compact',
            compactDisplay: 'short',
        }).format(num)
    }

    return new Intl.NumberFormat('en-US').format(num)
}

/**
 * Generates a Gravatar URL from an email address
 * @param email - The email address
 * @param size - The size of the avatar (default: 80)
 * @param defaultImage - The default image type (default: 'mp' for mystery person)
 * @param rating - The rating filter (default: 'g' for general audience)
 * @returns Gravatar URL
 */
export const getGravatarUrl = (
    email: string,
    size: number = 80,
    defaultImage: string = 'mp',
    rating: string = 'g'
): string => {
    if (!email) {
        return `https://www.gravatar.com/avatar/?d=${defaultImage}&s=${size}&r=${rating}`
    }

    // Create MD5 hash of the email (lowercase and trimmed)
    const emailHash = md5(email.toLowerCase().trim())

    return `https://www.gravatar.com/avatar/${emailHash}?d=${defaultImage}&s=${size}&r=${rating}`
}

/**
 * Generates a Gravatar URL with common presets
 * @param email - The email address
 * @param preset - The preset size ('small', 'medium', 'large')
 * @returns Gravatar URL
 */
export const getGravatarUrlPreset = (
    email: string,
    preset: 'small' | 'medium' | 'large' = 'medium'
): string => {
    const sizes = {
        small: 32,
        medium: 80,
        large: 200
    }

    return getGravatarUrl(email, sizes[preset])
}

/**
 * Converts URLs in text to clickable links with security attributes
 * @param text - The text containing potential URLs
 * @returns JSX elements with clickable links
 */
